import * as d3 from 'd3';
import { area, BaseType, NumberValue } from 'd3';
import { exportSvgAsPng, ISharedD3ChartProps, keyValueTitleTooltip, placeTooltip, wrapLine } from '../../vis-shared';

import { Size, Margin } from '../../visTypes';
import { layoutUniformWidth } from '../../force-layout';

export interface IBaseDatum {
  label: string;
  value: number;
}

export interface ISeries<IDatum extends IBaseDatum> {
  id: string;
  label: string;
  data: IDatum[];
  strokeDasharray?: string;
}

export interface ILineChartConfig<IDatum extends IBaseDatum> extends ISharedD3ChartProps {
  target: any;
  series: ISeries<IDatum>[];
  min?: number;
  max?: number;
  height?: number;
  width?: number;
  color?: (d: ISeries<IDatum>) => string;
  colorType?: 'health';
  valueAccessor?: (d: IDatum) => number;
  xField?: (d: IDatum, i: number) => number;
  xType?: 'quantitative' | 'temporal';
  pointRadius?: number;
  valueFormat?: (d: number) => string;
}

const css = `
text {
  font-family: 'Manrope', 'Inter', 'Inter var', sans-serif;
}
rect.background {
  fill: #F9FAFF;
  fill: none;
  fill: #F8EFE644;
}
.y.grid .domain {
  stroke: none;
}
.y.grid .tick line {
  stroke: #F8EFE6;
}
.y.axis .domain {
  stroke: none;
}
.y.axis .tick line {
  stroke: #F8EFE6;
}
.y.axis .tick text {
  fill: #6D6156;
  font-size: 12px;
  font-weight: 500;
}
.x.axis .domain {
  stroke: #F8EFE6;
  stroke: none;
}
.x.axis .tick line {
  stroke: #F8EFE6;
}
.x.axis .tick text {
  fill: #6D6156;
  font-size: 12px;
  font-weight: 500;
}
g.chart-selection.focused g.series {
  opacity: 0.15;
}
g.chart-selection.focused g.series circle {
  opacity: 0;
}
g.chart-selection.focused g.series.focused circle {
  opacity: 1;
}
g.chart-selection g.series text {
  opacity: 0;
}
g.chart-selection.focused g.series text {
  opacity: 0;
}
g.chart-selection.focused g.series.focused {
  opacity: 1;
}
g.chart-selection.focused g.series.focused text {
  opacity: 1;
}
g.chart-selection.focused g.label text {
  opacity: 0.2;
}
g.chart-selection.focused g.label.focused text {
  opacity: 1;
}
`;

const chartFontSize = 11;
let labelWidth = 150;

export class LineChart<IDatum extends IBaseDatum> {
  target: any;
  size: Size;
  chartSize: Size;
  margin: Margin;
  svg: d3.Selection<any, any, any, any>;
  chartSelection: d3.Selection<SVGGElement, any, any, any>;
  tooltip: d3.Selection<HTMLDivElement, any, any, any>;
  exportFileName: string;
  exportTitle: string | null;
  series: ISeries<IDatum>[];
  seriesSelection!: d3.Selection<SVGGElement, ISeries<IDatum>, SVGGElement, any>;
  xScale: d3.ScaleLinear<number, number> | d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  xAxis!: d3.Axis<NumberValue>;
  xAxisSelection: d3.Selection<SVGGElement, any, any, any>;
  color: (d: ISeries<IDatum>) => string;
  colorType?: string;
  valueAccessor: (d: IDatum) => number;
  valueFormat: (d: number) => string;
  xField: (d: IDatum, i: number) => number;
  xType: 'quantitative' | 'temporal';
  pointRadius?: number;
  focusId?: string;

  constructor(config: ILineChartConfig<IDatum>) {
    this.target = config.target;
    this.series = config.series;
    this.exportFileName = config.exportFileName || 'LineChart';
    this.exportTitle = config.exportTitle || null;
    this.pointRadius = config.pointRadius;
    this.colorType = config.colorType;
    this.xType = config.xType || 'quantitative';
    this.valueFormat = config.valueFormat || d3.format('.2s');
    const domWidth = Math.floor(parseInt(d3.select(this.target).style('width'), 10));
    const width = config.width || domWidth;
    const height = config.height || 300;
    this.size = {
      width,
      height,
    };

    const labelLengths = this.series.map((s) => s.label.length);
    const longestLabel = Math.max(...labelLengths);
    labelWidth = Math.min(250, longestLabel * chartFontSize * 0.6);
    // const rightMargin = 0; // this.calculateNeededMargin(config.nodes.map((d) => d.label));
    this.margin = {
      top: 25,
      bottom: 35,
      left: 45,
      right: 15 + labelWidth,
    };
    this.chartSize = {
      width: width - this.margin.left - this.margin.right,
      height: height - this.margin.top - this.margin.bottom,
    };

    this.svg = d3
      .select(this.target)
      .style('position', 'relative')
      .append('svg')
      .classed('line-chart', true)
      .attr('height', this.size.height)
      .attr('width', this.size.width);

    this.svg.append('style').text(css);

    this.chartSelection = this.svg
      .append('g')
      .attr('class', 'chart-selection')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    this.chartSelection
      .append('rect')
      .attr('class', 'background')
      .attr('width', this.chartSize.width)
      .attr('height', this.chartSize.height)
      .attr('rx', 10)
      .attr('ry', 10);

    this.tooltip = d3
      .select('body')
      .append('div')
      .style('visibility', 'hidden')
      .style('width', '160px')
      .style('font-size', '0.8em')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('z-index', 150);

    // @ts-ignore
    this.color = config.color || ((d: ISeries<IDatum>) => (d.color ? d.color : 'steelblue'));

    this.valueAccessor = config.valueAccessor || ((d: IDatum) => d.value);
    const allValues = this.series.reduce((acc, s) => {
      return acc.concat(s.data.map(this.valueAccessor));
    }, [] as number[]);

    const yMin = config.min !== undefined ? config.min : d3.min(allValues) || 0;
    const yMax = config.max !== undefined ? config.max : d3.max(allValues) || 0;

    this.yScale = d3.scaleLinear().domain([yMin, yMax]).range([this.chartSize.height, 0]);

    this.xField = config.xField || ((d, i) => i);
    const xValues = this.series.reduce((acc, s) => {
      return acc.concat(s.data.map(this.xField));
    }, [] as number[]);

    const xMin = d3.min(xValues) || 0;
    const xMax = d3.max(xValues) || 0;

    this.xScale = d3.scaleLinear().domain([xMin, xMax]).range([0, this.chartSize.width]);

    if (this.xType === 'temporal') {
      this.xScale = d3.scaleTime().domain([xMin, xMax]).range([0, this.chartSize.width]);
    }

    this.xAxisSelection = this.chartSelection;
    // .append('g')
    // .attr('class', 'x axis')
    // .attr('transform', `translate(0, ${this.chartSize.height / 2})`);

    if (config.colorType === 'health') {
      const yValueMin = d3.min(allValues) || 0;
      const yValueMax = d3.max(allValues) || 0;

      // add a gradient def to svg, going from red to green
      const gradient = this.svg
        .append('defs')
        .append('linearGradient')
        .attr('id', 'healthGradient')
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');

      const colorBad = '#FC466B';
      const colorGood = '#3F5EFB';
      const colorScoreScale = 10;

      // Calculate the colors for the actual yMin and yMax using d3 color interpolation
      const colorScale = d3.interpolateRgb(colorBad, colorGood);
      const colorMin = colorScale(yValueMin / 10);
      const colorMax = colorScale(yValueMax / 10);

      console.log({ colorMin, colorMax });

      gradient.append('stop').attr('offset', '0%').attr('stop-color', colorMax);
      gradient.append('stop').attr('offset', '100%').attr('stop-color', colorMin);
    }

    this.draw();
  }

  draw(): void {
    this.drawAxes();
    this.drawLabels();

    const x = (d: IDatum, i: number) => this.xScale(this.xField(d, i));
    const y = (d: IDatum) => this.yScale(this.valueAccessor(d));

    const calcLinePath = d3.line<IDatum>().curve(d3.curveCardinal.tension(0.5)).x(x).y(y);

    this.seriesSelection = this.chartSelection.selectAll('g.series').data(this.series).enter().append('g').attr('class', 'series');

    this.seriesSelection
      .append('path')
      .attr('class', 'line')
      .attr('d', (d) => calcLinePath(d.data))
      .attr('fill', 'none')
      .attr('stroke', (d) => (this.colorType === 'health' ? 'url(#healthGradient)' : this.color(d)))
      .attr('stroke-width', 3)
      .attr('stroke-dasharray', (d) => d.strokeDasharray || '');

    if (this.pointRadius) {
      this.seriesSelection
        .selectAll('.point')
        .data((p) => p.data.map((d) => ({ ...d, parent: p })))
        .enter()
        .append('circle')
        .attr('class', 'point')
        .attr('cx', x)
        .attr('cy', y)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .attr('fill', (d) => (this.colorType === 'health' ? 'url(#healthGradient)' : this.color(d.parent)))
        .attr('r', this.pointRadius);
    }

    // add value labels
    this.seriesSelection
      .selectAll('.value-label')
      .data((p) => p.data.map((d) => ({ ...d, parent: p })))
      .enter()
      .append('text')
      .attr('class', 'value-label value-bg')
      .attr('x', x)
      .attr('y', y)
      .attr('dy', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', chartFontSize * 1.15)
      .attr('font-weight', 800)
      .attr('fill', (d) => (this.colorType === 'health' ? 'url(#healthGradient)' : this.color(d.parent)))
      .attr('stroke', 'white')
      .attr('stroke-width', '4px')
      .text((d) => this.valueFormat(this.valueAccessor(d)));

    // add value labels
    this.seriesSelection
      .selectAll('.value-label.value-text')
      .data((p) => p.data.map((d) => ({ ...d, parent: p })))
      .enter()
      .append('text')
      .attr('class', 'value-label value-text')
      .attr('x', x)
      .attr('y', y)
      .attr('dy', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', chartFontSize * 1.15)
      .attr('font-weight', 800)
      .attr('fill', (d) => (this.colorType === 'health' ? 'url(#healthGradient)' : this.color(d.parent)))
      .text((d) => this.valueFormat(this.valueAccessor(d)));
  }

  drawLabels(): void {
    // append labels to the end of each line

    // 1. Get the last data point for each series
    const labeledSeriesKeys = this.series
      .filter((d) => d.label.length)
      .map((s) => {
        const lastDatum = s.data[s.data.length - 1];
        const yAnchor = this.yScale(this.valueAccessor(lastDatum));
        return {
          id: s.id,
          label: s.label,
          value: this.valueAccessor(lastDatum),
          yAnchor,
          yPlacement: yAnchor,
        };
      });

    // 2. Use a force directed layout to position the labels so that they don't overlap
    const labelHeight = chartFontSize * 1.35;
    const placements = layoutUniformWidth(
      labeledSeriesKeys.map((d) => d.yAnchor),
      labelHeight,
      -8,
      this.chartSize.height + 10
    );
    labeledSeriesKeys.forEach((d, i) => {
      d.yPlacement = placements[i];
    });

    // 3. Draw the labels
    const labelSelection = this.chartSelection
      .selectAll('g.label')
      .data(labeledSeriesKeys)
      .enter()
      .append('g')
      .attr('class', 'label')
      .attr('transform', (d) => `translate(${this.chartSize.width + 10}, ${d.yPlacement})`)
      .on('click', (event, d) => {
        this.toggleFocus(d.id);
      });

    labelSelection
      .append('text')
      .attr('class', 'label')
      .attr('x', 0)
      .attr('y', 0)
      .attr('dy', '0.15em')
      .attr('text-anchor', 'start')
      .attr('font-weight', 600)
      .attr('font-size', chartFontSize)
      .style('cursor', 'pointer')
      .text((d) => d.label)
      // eslint-disable-next-line
      .each(function (d: any) {
        wrapLine(d3.select(this), labelWidth);
      });

    // 4. Draw the lines from the last data point to the labels
    // const lineSelection = this.chartSelection
    //   .selectAll('g.label-line')
    //   .data(labeledSeriesKeys)
    //   .enter()
    //   .append('g')
    //   .attr('class', 'label-line')
    //   .attr('transform', (d) => `translate(${this.chartSize.width}, ${d.yAnchor})`);

    // lineSelection
    //   .append('line')
    //   .attr('class', 'label-line')
    //   .attr('x1', 0)
    //   .attr('y1', 0)
    //   .attr('x2', 10)
    //   .attr('y2', 0)
    //   .attr('stroke', 'black')
    //   .attr('stroke-width', 1);
  }

  drawAxes(): void {
    let xAxis = d3.axisBottom(this.xScale).tickSizeOuter(0).tickFormat(d3.format('.2s')).ticks(5);

    if (this.xType === 'temporal') {
      if (this.colorType === 'health') {
        xAxis = d3.axisBottom(this.xScale).tickSizeOuter(0).ticks(d3.timeMonth.every(1));
      } else {
        xAxis = d3
          .axisBottom(this.xScale)
          .tickSizeOuter(0)
          // .tickFormat(d3.timeFormat('%b %d'))
          .ticks(5);
      }
    }

    this.chartSelection
      .append('g')
      .attr('class', 'x axis')
      .attr('transform', `translate(0, ${this.chartSize.height + 4})`)
      .call(xAxis);

    const yAxis = d3
      .axisLeft(this.yScale)
      .tickSizeOuter(0)
      // @ts-ignore
      .tickFormat(this.valueFormat)
      .ticks(5);

    this.chartSelection.append('g').attr('class', 'y axis').attr('transform', `translate(-6, 0)`).call(yAxis);

    this.chartSelection.append('g').attr('class', 'y grid').call(
      d3
        .axisLeft(this.yScale)
        .tickSize(-this.chartSize.width)
        // @ts-ignore
        .tickFormat('')
        .ticks(5)
    );
  }

  toggleFocus(seriesId?: string): void {
    if (!seriesId) {
      this.unfocus();
      this.focusId = undefined;
      return;
    }
    if (this.focusId === seriesId) {
      this.unfocus();
      this.focusId = undefined;
    } else {
      this.focusSeries(seriesId);
      this.focusId = seriesId;
    }
  }

  unfocus(): void {
    this.chartSelection.classed('focused', false);
    this.seriesSelection.classed('focused', false);
    d3.selectAll('g.label').classed('focused', false);
    d3.selectAll('g.label').classed('focused', false);
  }

  focusSeries(seriesId: string): void {
    this.chartSelection.classed('focused', true);
    this.seriesSelection.classed('focused', false);
    this.seriesSelection
      .filter((d) => {
        return d.id === seriesId;
      })
      .classed('focused', true);

    d3.selectAll('g.label').classed('focused', false);
    d3.selectAll('g.label')
      .filter((d: any) => {
        return d.id === seriesId;
      })
      .classed('focused', true);
  }

  exportToPng(): void {
    const customCss = `text { font-family: 'Hanken Grotesk', sans-serif }`;
    exportSvgAsPng(this.svg.node(), {
      customCss,
      scale: 3,
      fileName: this.exportFileName,
      title: this.exportTitle || undefined,
    });
  }
}
